import React, { useEffect, Fragment } from "react";
import ConnectedTextarea from "components/ConnectedTextarea";
import { useFormikContext } from "formik";
import Analytics, { COPY_TAX_RATE_ID } from "utils/analytics";
import { getFieldPath } from "utils/form";
import get from "lodash/get";
import CopyToClipboard from "components/CopyToClipboard";
import Token from "components/Token";
import SectionBezel from "components/SectionBezel";
import classnames from "classnames";
import { getStripeDestination } from "utils/stripe";
import StripeExternalLink from "components/StripeExternalLink";
import { useCtxReferences } from "utils/selectors";
import { getAccountConnectStatusProps } from "utils/merchantAccount";
import SelectField from "components/SelectField";
import { FORM_KEY } from "utils/constants/form";
import {
  TAX_RATE_DISPLAY_NAME,
  TAX_RATE_DISPLAY_NAME_OPTIONS,
  TAX_RATE_FORM_KEY
} from "utils/constants/taxRate";

/**
 * Set
 * - description
 * - jurisdiction
 * @param {String} name
 */
const DetailsFields = ({ name }) => {
  const { values, setFieldValue } = useFormikContext();

  const displayNameFieldPath = getFieldPath(name, "display_name");
  const descriptionFieldPath = getFieldPath(name, "description");
  const jurisdictionFieldPath = getFieldPath(name, "jurisdiction");

  const displayNameTypeFormPath = getFieldPath(
    name,
    `${FORM_KEY}.${TAX_RATE_FORM_KEY.DISPLAY_NAME_TYPE}`
  );
  const displayNameType = get(values, displayNameTypeFormPath);

  const idPath = getFieldPath(name, "id");
  const id = get(values, idPath);
  const editMode = Boolean(id);

  const displayNamePath = getFieldPath(name, "display_name");

  const { merchantAccount } = useCtxReferences();

  const account =
    merchantAccount && getAccountConnectStatusProps(merchantAccount);

  const actionNode = editMode ? (
    <StripeExternalLink
      account={account}
      copy={` `}
      tooltip="Manage on Stripe"
      href={getStripeDestination({ path: `tax-rates/${id}` })}
    />
  ) : null;

  /**
   * If type changes to CUSTOM - unset the display_name
   * Else set the default display label per the type label
   * Note: display_name type is not editable
   */
  const isCustom = displayNameType === TAX_RATE_DISPLAY_NAME.CUSTOM;
  useEffect(() => {
    if (!editMode) {
      if (isCustom) {
        setFieldValue(displayNamePath, "");
      } else {
        const typeMatch = TAX_RATE_DISPLAY_NAME_OPTIONS.find(
          (option) => option.uid === displayNameType
        );

        if (typeMatch && typeMatch.label) {
          setFieldValue(displayNamePath, typeMatch.label);
        }
      }
    }
  }, [displayNameType]);

  /**
   * User can change the display name
   * - We use a constrained set of options to enforce some standardization
   * - but also allow for setting a custom value
   */
  let displayNameNodes = [
    <SelectField
      key="display-name-type"
      disabled={editMode}
      name={displayNameTypeFormPath}
      label="Display name"
      options={TAX_RATE_DISPLAY_NAME_OPTIONS}
      customClasses={{ label: "field__label fw5 db" }}
    />
  ];

  if (isCustom) {
    displayNameNodes.push(
      <ConnectedTextarea
        key="display-name-custom"
        minRows={1}
        name={displayNameFieldPath}
        placeholder="Enter your custom tax rate display name"
      />
    );
  }

  return (
    <SectionBezel header="Details" action={actionNode}>
      {displayNameNodes.map((node, nodeIx) => (
        <div
          key={nodeIx}
          className={classnames("w-100", {
            pt3: nodeIx
          })}
        >
          {node}
        </div>
      ))}
      <div className="pt3">
        <ConnectedTextarea
          minRows={1}
          label="Description"
          name={descriptionFieldPath}
          placeholder="Optional description"
        />
      </div>
      <div className="pt3">
        <ConnectedTextarea
          minRows={1}
          label="Jurisdiction"
          name={jurisdictionFieldPath}
          placeholder="Optional jurisdiction"
        />
      </div>
      {editMode && (
        <Fragment>
          <div className="pt3">
            <label className="field__label fw5 db">ID</label>
            <div className="flex flex-row items-center">
              <CopyToClipboard
                tooltip={`Copy id`}
                toast="Copied id to clipboard"
                value={id}
                onClick={() => {
                  Analytics.track(COPY_TAX_RATE_ID, {
                    id
                  });
                }}
              />
              <Token value={id} />
            </div>
          </div>
        </Fragment>
      )}
    </SectionBezel>
  );
};

export default DetailsFields;
